import {getCountryCodeAndNameFromValue} from '@services/new/common/getLeadMeta';
import {isValidPhoneNumber} from '@utils/validation.utils';
import * as yup from 'yup';

export type InputNames =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'contact'
  | 'countryCode'
  | 'consent';

export const GA_DARNA_METADATA = {
  formId: 'darna_join_form',
  formType: 'darna_join_form',
};

export const BOOLEAN_INPUTS = ['consent'];

export const parseDataForSubmit = (data: Record<InputNames, string>) => {
  const booleanSubObj = Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      if (BOOLEAN_INPUTS.includes(key)) {
        return [key, Boolean(value)];
      }
      return [key, value];
    })
  );
  const {code} = getCountryCodeAndNameFromValue(data.countryCode!!);
  return {...booleanSubObj, countryCode: code};
};

export const validationSchema = yup.object({
  firstName: yup
    .string()
    .trim()
    .max(35, 'First name is too long')
    .matches(/^[A-Za-z]+(?:\s[A-Za-z]+)*$/, 'First name is invalid')
    .required('First name is required'),
  lastName: yup
    .string()
    .trim()
    .max(35, 'Last name is too long')
    .matches(/^[A-Za-z]+(?:\s[A-Za-z]+)*$/, 'Last name is invalid')
    .required('Last name is required'),
  countryCode: yup.string().required('Country code is required'),
  contact: yup
    .string()
    .test(
      'Phone Number Leading Zero validation check',
      'Kindly enter the phone number in international format',
      function (number) {
        const phoneNumber = number as string;
        return !phoneNumber.trim().startsWith('0');
      }
    )
    .test(
      'Phone Number Validity Check',
      'Not a valid phone number',
      function (number) {
        const {code} = getCountryCodeAndNameFromValue(this.parent.countryCode);
        return isValidPhoneNumber(number as string, code);
      }
    )
    .required('Phone number is required'),
  email: yup
    .string()
    .required('Email is required')
    .email('Invalid email format'),
  nationality: yup.string().when('needCall', {
    is: (needCall: string) => needCall === 'true',
    then: (schema) => schema.required('Nationality is required'),
    otherwise: (schema) => schema,
  }),
  language: yup.string().when('needCall', {
    is: (needCall: string) => needCall === 'true',
    then: (schema) => schema.required('Language is required'),
    otherwise: (schema) => schema,
  }),
  needCall: yup.string(),
  consent: yup
    .string()
    .required('Consent to Privacy Policy & Terms and Conditions to continue'),
});

export function parseValidationErrors(
  validationError: any
): Record<InputNames, string> {
  let errorMessages: Record<string, string> = {};
  validationError.inner.forEach((error: yup.ValidationError) => {
    if (error.path) {
      errorMessages[error.path] = error.message;
    }
  });
  return errorMessages;
}
